/**
 * Shared types and value constructors for pricing tables.
 * Used by both api-pricing-data.ts and agents-pricing-data.ts
 */

/** Unit types for currency values */
export type CurrencyUnit =
  | "per_hour"
  | "per_minute"
  | "per_minute_approx"
  | "per_message"
  | "per_1k_characters"
  | "per_generation"
  | "flat"
  | "flat_approx";

/** Structured value types for the view layer to format */
export type FormattedValue =
  | { type: "number"; value: number | undefined; precision?: number }
  | { type: "currency"; value: number | undefined; unit: CurrencyUnit; precision?: number }
  | { type: "hours"; value: number | undefined }
  | { type: "minutes"; value: number | undefined }
  | { type: "minutes_approx"; value: number | undefined; suffix?: "min" | "none" }
  | { type: "characters"; value: number | undefined }
  | { type: "text"; textKey: string; params?: Record<string, string | number> }
  | { type: "boolean"; value: boolean }
  | { type: "link"; textKey: string; href: string; target?: string }
  | { type: "unavailable" };

// Value constructors

/** Creates a number value, or unavailable if undefined */
export const numberValue = (value: number | undefined, precision?: number): FormattedValue =>
  value === undefined ? { type: "unavailable" } : { type: "number", value, precision };

/** Creates a currency value with unit and optional precision, or unavailable if undefined or 0 */
export const currencyValue = (
  value: number | undefined,
  unit: CurrencyUnit = "flat",
  precision?: number,
): FormattedValue =>
  value === undefined || value === 0 ? { type: "unavailable" } : { type: "currency", value, unit, precision };

/** Creates an hours value, or unavailable if undefined or 0 */
export const hoursValue = (value: number | undefined): FormattedValue =>
  value === undefined || value === 0 ? { type: "unavailable" } : { type: "hours", value };

/** Creates a minutes value, or unavailable if undefined or 0 */
export const minutesValue = (value: number | undefined): FormattedValue =>
  value === undefined || value === 0 ? { type: "unavailable" } : { type: "minutes", value };

/** Creates an approximate minutes value (rendered with ~ prefix), or unavailable if undefined or 0 */
export const minutesApproxValue = (value: number | undefined, suffix: "min" | "none" = "min"): FormattedValue =>
  value === undefined || value === 0 ? { type: "unavailable" } : { type: "minutes_approx", value, suffix };

/** Creates a characters value, or unavailable if undefined */
export const charactersValue = (value: number | undefined): FormattedValue =>
  value === undefined ? { type: "unavailable" } : { type: "characters", value };

/** Creates a text value with a translation key and optional interpolation params */
export const textValue = (textKey: string, params?: Record<string, string | number>): FormattedValue => ({
  type: "text",
  textKey,
  params,
});

/** Creates a boolean value (rendered as checkmark or cross) */
export const booleanValue = (value: boolean): FormattedValue => ({ type: "boolean", value });

/** Creates a link value with a translation key for the label */
export const linkValue = (textKey: string, href: string, target?: string): FormattedValue => ({
  type: "link",
  textKey,
  href,
  target,
});
